import React from 'react';
import { clsx } from 'clsx';

import type { SaveListItem } from '../../../../../types/save-types.js';
import type { TeaserWithCoords, TopListView } from '../types.js';

import {
  restSaveTags,
  topSaveTags,
} from '../../../../components/articles/helpers.js';
import { useListsContext } from '../../../../components/saving/ListsProvider.js';
import { Save } from '../../../../components/saving/Save.js';
import { itemIsSaved } from '../../../../components/saving/utils.js';
import { useTeaserSaveOverlay } from '../../../../components/teasers/hooks/useTeaserSaveOverlay.js';
import Image from '../../../../components/teasers/teaserComponents/Image.js';
import TeaserGrade from '../../../../components/teasers/teaserComponents/TeaserGrade.js';
import TeaserTag from '../../../../components/teasers/teaserComponents/TeaserTag.js';
import TeaserWrapper from '../../../../components/teasers/TeaserWrapper.js';
import { useForwardedRef } from '../../../../hooks/useForwardedRef.js';
import { useIntersectionObserver } from '../../../../hooks/useIntersectionObserver.js';
import { OpenMapButton } from './OpenMapButton.js';

type TopListTeaserProps = {
  teaser: TeaserWithCoords;
  ref: React.Ref<HTMLDivElement>;
  view: TopListView;
  onView?: (articleId: string) => void;
};

const TopListTeaser: React.FC<TopListTeaserProps> = ({
  teaser,
  ref,
  view,
  onView,
}) => {
  const teaserRef = useForwardedRef(ref);

  useIntersectionObserver(teaserRef, () => onView?.(teaser.id));

  const { savedItems } = useListsContext();

  const trackObject = {
    objectId: 'save-button',
    name: 'top list save button',
    elementType: 'Button',
  };

  const listTeaser: SaveListItem = {
    id: teaser.id,
    title: teaser.kicker ?? teaser.title,
    imageId: teaser.image?.id,
    slug: teaser.slug,
    kicker: topSaveTags(teaser) ?? teaser.section,
    ...(teaser.grade && { grade: teaser.grade }),
    tags: restSaveTags(teaser),
  };

  const isSaved = itemIsSaved(listTeaser, savedItems);

  const { handleSave, handleOverlay, overlay } = useTeaserSaveOverlay(
    listTeaser,
    trackObject,
  );

  const { latitude, longitude } = teaser.restaurantCoordinates;

  return (
    <div
      className={`top-list__teaser-wrapper top-list__teaser-wrapper--${view}`}
    >
      {overlay.overlayVisible && (
        <Save handleOverlay={handleOverlay} item={overlay.itemToSave} />
      )}
      <TeaserWrapper classNames="top-list__teaser" teaser={teaser}>
        <div ref={teaserRef} className="top-list__teaser-info">
          {teaser.image && (
            <figure className="top-list__teaser-image">
              <Image
                imageAsset={teaser.image}
                width={120}
                height={120}
                title={teaser.title}
              />
            </figure>
          )}
          <div>
            <div className="top-list__teaser-header">
              {teaser.kicker && (
                <h3 className="top-list__teaser-kicker">{teaser.kicker}</h3>
              )}
            </div>
            {teaser.grade && (
              <div className="grade-tags">
                <TeaserGrade grade={teaser.grade} />
                <TeaserTag tags={teaser.tags} section={teaser.section} />
              </div>
            )}
          </div>
        </div>
      </TeaserWrapper>
      <div className="top-list__teaser-actions">
        <OpenMapButton
          className="top-list__teaser-address-button"
          latitude={latitude}
          longitude={longitude}
          locationName={teaser.kicker}
          url={teaser.googleMapsLink}
        >
          Adresse
        </OpenMapButton>
        <button
          className={clsx(
            'top-list__teaser-save-button',
            'top-list__teaser-save-button--save',
            isSaved && 'top-list__teaser-save-button--saved',
          )}
          onClick={handleSave}
        >
          {isSaved ? 'Lagret' : 'Lagre'}
        </button>
      </div>
    </div>
  );
};

export { TopListTeaser };
